import type { ComputedRef, InjectionKey, Ref } from 'vue'
import { inject } from 'vue'
import type {
  DockOption, DraftValidationIssue, DraftWaypoint, DroneProfile, EditorHeightMode, NearGroundIssue, PayloadLens, PayloadProfile,
  RouteStatistics, WaylineDraft, WaypointActionType,
} from '/@/types/wayline-editor'
import type { AircraftSpeedMode } from '/@/utils/wayline-editor'

export type EditorTool = 'select' | 'add' | 'takeoff' | 'poi'
export type ViewMode = 'free' | 'follow' | 'fpv'

/** Virtual aircraft the operator flies to lay out the route. */
export interface AircraftState {
  active: boolean
  longitude: number
  latitude: number
  /** Height in the draft height mode: ALT metres or ellipsoid metres. */
  height: number
  heading: number
  gimbalPitch: number
  speedMode: AircraftSpeedMode
  /** Lens shown in the camera preview; zoom uses the payload's long focal length. */
  lens: PayloadLens
}

/** Map display and editing preferences (session only). */
export interface DisplaySettings {
  showWaypoints: boolean
  showGimbal: boolean
  showDropLines: boolean
  thickLines: boolean
  /** Record heading and gimbal pitch into each waypoint added from the aircraft. */
  recordAttitude: boolean
  /** Minimum height above terrain before a 近地提示 is raised, metres. */
  minAgl: number
  /** Show the map straight down (2D) instead of the oblique 3D view. */
  flat: boolean
}

export interface WaylineEditorContext {
  draft: WaylineDraft
  sourceWaylineId: string | undefined
  droneProfile: ComputedRef<DroneProfile | undefined>
  payloadProfile: ComputedRef<PayloadProfile | undefined>
  heightMode: ComputedRef<EditorHeightMode>
  selectedIds: Ref<string[]>
  selected: ComputedRef<DraftWaypoint[]>
  /** Index of the action shown in the right panel; -1 shows the waypoint's own parameters. */
  activeActionIndex: Ref<number>
  stats: ComputedRef<RouteStatistics>
  issues: ComputedRef<DraftValidationIssue[]>
  nearGround: ComputedRef<NearGroundIssue[]>
  groundHeights: Ref<Record<string, number | null>>
  aircraft: AircraftState
  /** Terrain height under the aircraft in scene metres (the frame the map draws in). */
  aircraftGround: Ref<number | null>
  /** Height of the aircraft above the terrain, or above the pad plane where no terrain is installed. */
  aircraftAgl: ComputedRef<number>
  /** Subtracted from ellipsoid heights before drawing, so the pad stands on the
   * terrain under it (plus its declared height above ground) or, without
   * terrain, on the reference surface. */
  sceneOffset: ComputedRef<number>
  /** False while the map is the bare reference ellipsoid. */
  terrainAvailable: Ref<boolean>
  /** Most detailed terrain height under the takeoff point, ellipsoid metres; null while unknown. */
  padTerrain: Ref<number | null>
  /** Reported pad ellipsoid height minus terrain plus declared AGL height; null while unknown. */
  padHeightGap: ComputedRef<number | null>
  pressedKeys: Ref<Record<string, boolean>>
  settings: DisplaySettings
  tool: Ref<EditorTool>
  viewMode: Ref<ViewMode>
  cameraHeading: Ref<number>
  docks: Ref<DockOption[]>
  loadingDocks: Ref<boolean>
  saving: Ref<boolean>
  dirty: Ref<boolean>
  canUndo: ComputedRef<boolean>
  rightPanelOpen: Ref<boolean>

  mutate (change: () => void): void
  commit (): void
  undo (): void
  setName (name: string): void
  setTool (tool: EditorTool): void
  pickPoi (target: 'global' | 'selected'): void
  setViewMode (mode: ViewMode): void
  setFlat (flat: boolean): void
  setHeightMode (mode: EditorHeightMode): void

  selectWaypoint (id: string, range?: boolean): void
  selectByOffset (offset: number): void
  selectActionByOffset (offset: number): void
  reorderWaypoints (from: number, to: number): void
  removeWaypoint (id: string): void
  removeSelected (): void
  toggleRisky (id: string): void
  reverseRoute (): void
  copyActions (id: string): void
  applyActionTemplate (id: string): void
  hasActionTemplate: ComputedRef<boolean>
  setWaypointField (key: keyof DraftWaypoint, value: unknown): void
  setWaypointNested (group: 'turnParam' | 'headingParam', key: string, value: unknown): void
  addAction (type: WaypointActionType): void
  removeAction (actionId: string): void
  moveAction (actionId: string, direction: 1 | -1): void
  updateAction (actionId: string, params: Record<string, string | number | boolean>): void

  addVertexAt (longitude: number, latitude: number): void
  removeVertex (id: string): void
  clearVertices (): void

  takeOff (): void
  landAircraft (): void
  addWaypointAtAircraft (): void
  orientedShotAtAircraft (): void
  panoShotAtAircraft (): void
  recordAttitudeAtAircraft (): void
  actionAtAircraft (type: WaypointActionType): void
  moveAircraftToSelected (): void
  toggleSpeedMode (): void
  setGimbalPitch (pitch: number): void
  setAircraftLens (lens: PayloadLens): void
  setAircraftHeight (height: number): void
  setAircraftHeading (heading: number): void
  moveSelectedTo (longitude: number, latitude: number): void

  loadDocks (): Promise<void>
  useDock (sn: string): void
  clearTakeoff (): void
  /** Re-sample the terrain height under a pad that was picked on the map. */
  refinePadHeight (): Promise<void>
  fitRoute (): void
  flyToSelected (): void
  zoom (direction: number): void
  resetNorth (): void
  locateMe (): void
  save (): Promise<void>
  exportKmz (): Promise<void>
  requestClose (): void
}

// Symbol.for keeps provide/inject paired across HMR re-evaluations of this module.
export const WAYLINE_EDITOR_KEY: InjectionKey<WaylineEditorContext> = Symbol.for('wayline-editor')

export function useWaylineEditor (): WaylineEditorContext {
  const context = inject(WAYLINE_EDITOR_KEY)
  if (!context) throw new Error('useWaylineEditor must be used inside WaylineEditor')
  return context
}
